import dayjs from 'dayjs';
import toast from 'react-hot-toast';

import React, { useState, useEffect, useRef } from 'react';

import { Form, Card, Button, InputGroup, Spinner, Alert } from 'react-bootstrap';
import { Link, useHistory } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEnvelope,
  faUnlockAlt,
  faEye,
  faEyeSlash,
} from '@fortawesome/free-solid-svg-icons';

import { diffFromNow, singleTruthNow } from '@/utils/format';

import AuthLayout from '../features/auth/components/AuthLayout';

import { Routes } from '../routes';
import { useAuth } from '../lib/auth';

type FormInputs = {
  id?: string;
  password: string;
  email: string;
};

const MAX_LOGIN_ATTEMPTS = 3;

const emptyAccount = {
  password: '',
  email: '',
};

const Login = () => {
  const history = useHistory();

  const [formData, setFormData] = useState<FormInputs>(emptyAccount);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [showMigrationBanner, setShowMigrationBanner] = useState(true);
  // login hook
  const { user, login, isLoggingIn } = useAuth();
  // ! Count failure for login attemps to block brute force attack:
  // @2025/09/28
  const attempsRef = useRef(0);

  useEffect(() => {
    const homePage = Routes.HomeScreen.path;
    const loginPage = Routes.SignIn.path;
    const target = user ? homePage : loginPage;
    history.push(target);
    // TODO:
    // !user && dropNotification();
  }, [user, history]);

  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.ChangeEvent<HTMLFormElement>) => {
    e.preventDefault();

    const key = 'tk-failed-timestamp';
    const lastBlokedTime = localStorage.getItem(key);
    const onehour = 1 * 3600 * 1000; // in miliseconds
    // ! == first: within block-period test!
    if (lastBlokedTime) {
      const diff = diffFromNow(lastBlokedTime);
      if (diff < onehour) return console.warn(`## Block another login attemp!`);
    }
    // ! == then: max attemps test!
    if (attempsRef.current >= MAX_LOGIN_ATTEMPTS) {
      // save failure time!
      localStorage.setItem(key, singleTruthNow());
      return toast.error(
        `## Maximum login attamps reached, you are blocked to further try!`
      );
    }
    // ! == last: available to login
    try {
      const res = await login(formData);
      if (res && res.id) {
        history.push(Routes.HomeScreen.path);
      }
    } catch (error: any) {
      // ! add one failure!
      attempsRef.current += 1;

      // ! could be error object from role checking step!
      // @2025/09/30
      if (!error.request) {
        return toast.error(error.message);
      }
      toast.error(
        `An error occurs: ${JSON.parse(error.request.responseText).detail}`
      );
    }
  };

  return (
    <>
      {showMigrationBanner && (
        <Alert
          variant="info"
          onClose={() => setShowMigrationBanner(false)}
          dismissible
          className="mb-0 text-center rounded-0"
        >
          We&apos;re migrating to a new admin dashboard. Give it a try:{' '}
          <Alert.Link
            href="https://taksoai-dashboard-taksoai.vercel.app/login"
            target="_blank"
            rel="noopener noreferrer"
            className="text-decoration-underline"
          >
            taksoai-dashboard-taksoai.vercel.app
          </Alert.Link>
        </Alert>
      )}
      <AuthLayout backButton={false} title="Log In">
        <Form className="mt-5" onSubmit={handleSubmit}>
          <Form.Group id="email" className="mb-4">
            <Form.Label>Email Address</Form.Label>
            <InputGroup>
              <InputGroup.Text>
                <FontAwesomeIcon icon={faEnvelope} />
              </InputGroup.Text>
              <Form.Control
                name="email"
                onChange={handleInputChange}
                autoFocus
                required
                type="email"
                placeholder="example@company.com"
                value={formData.email}
              />
            </InputGroup>
          </Form.Group>
          <Form.Group id="password" className="mb-4">
            <Form.Label>Password</Form.Label>
            <InputGroup>
              <InputGroup.Text>
                <FontAwesomeIcon icon={faUnlockAlt} />
              </InputGroup.Text>
              <Form.Control
                type={passwordVisible ? 'text' : 'password'}
                name="password"
                onChange={handleInputChange}
                required
                placeholder="Password"
                value={formData.password}
              />
              <InputGroup.Text
                onClick={togglePasswordVisibility}
                style={{
                  borderRight: '0.0625rem solid #d1d7e0',
                  cursor: 'pointer',
                }}
              >
                {passwordVisible ? (
                  <FontAwesomeIcon icon={faEyeSlash} />
                ) : (
                  <FontAwesomeIcon icon={faEye} />
                )}
              </InputGroup.Text>
            </InputGroup>
          </Form.Group>
          <div className="d-flex justify-content-end align-items-center mb-4">
            <Card.Link
              as={Link}
              className="link-primary small text-end"
              to={Routes.ForgotPassword.path}
            >
              Forgot Password?
            </Card.Link>
          </div>
          <div className="d-flex justify-content-center">
            <Button
              variant="primary"
              type="submit"
              className="mt-4 px-4 bg-blue w-24"
            >
              {isLoggingIn ? (
                <Spinner size="sm" animation="border" />
              ) : (
                `Log in`
              )}
            </Button>
          </div>
        </Form>
      </AuthLayout>
    </>
  );
};

export default Login;
